// Real case studies for Arvind Printers — photographs are actual work produced
// at the Chikalthana MIDC facility, hosted from /public/case-studies/photos/.
// Each case study carries a brand, sector, plausible short story, list of
// services delivered, and 1–3 real photos.

const P = "/case-studies/photos";

export const CASE_STUDIES = {
  // ── Automotive ─────────────────────────────────────────────────────────────
  "Royal Enfield": {
    sector: "Automotive",
    title: "Every touchpoint of the ride, on-brand",
    summary:
      "From the festive arch gate to service-bay SOPs and dispatch tags — a full ecosystem of branded print for the Royal Enfield dealership, engineered for both showroom drama and workshop durability.",
    services: [
      "Arch Gates",
      "Delivery Backdrops",
      "Service SOPs",
      "Product Tags",
      "Vinyl Signage",
    ],
    images: [
      `${P}/royal-enfield-arch-gate.jpeg`,
      `${P}/royal-enfield-delivery-backdrop.jpeg`,
      `${P}/royal-enfield-service-sop_2.jpeg`,
    ],
    accent: "#EC008C",
  },
  Mahindra: {
    sector: "Automotive",
    title: "Festival-season showroom arch",
    summary:
      "A larger-than-life festival arch gate for the Mahindra showroom — designed and printed in-house on eco-solvent flex, installed dealer-side for the Diwali sales drive.",
    services: ["Arch Gates", "Eco-Solvent Flex", "Showroom Branding"],
    images: [`${P}/mahindra-showroom-festival-arch-gate.jpeg`],
    accent: "#00AEEF",
  },
  Suzuki: {
    sector: "Automotive",
    title: "Festive-season showroom takeover",
    summary:
      "Complete festive-season showroom facade — printed flex, wall vinyls and glass films that transform the dealership floor into a launch stage.",
    services: ["Showroom Branding", "Glass Film", "Wall Vinyl", "Flex"],
    images: [`${P}/suzuki-festive-season-showroom-branding.jpeg`],
    accent: "#FFE500",
  },
  "Sanjeev Auto": {
    sector: "Automotive",
    title: "Plant identity, outside in",
    summary:
      "Outdoor branding and ACP-mounted plant boards for Sanjeev Auto's manufacturing campus — engineered for the sun-monsoon cycle of the region.",
    services: ["ACP Boards", "Outdoor Branding", "Plant Signage"],
    images: [
      `${P}/sanjeev_auto_outdoor_branding.jpeg`,
      `${P}/sanjeev-auto-plant-board_acp_2.jpeg`,
    ],
    accent: "#00AEEF",
  },

  // ── Hospitality / F&B ──────────────────────────────────────────────────────
  "ITC Hotel Rama": {
    sector: "Hospitality",
    title: "In-room to banquet — a print system",
    summary:
      "Japanese food menus, banquet folders, Christmas installations, tent cards, tray mats and parcel boxes for ITC Hotel Rama — a full in-house print system across F&B and events.",
    services: [
      "Menu Cards",
      "Banquet Folders",
      "Tent Cards",
      "Tray Mats",
      "Parcel Boxes",
      "Festive Installations",
    ],
    images: [
      `${P}/itc-hotel-rama_1.jpeg`,
      `${P}/itc-hotel-rama_japanese-food-menu_1.jpeg`,
      `${P}/itc-hotel-rama_christmas-tree.jpeg`,
    ],
    accent: "#D4AF37",
  },
  "Spree Hotel": {
    sector: "Hospitality",
    title: "Acrylic menus + facade branding",
    summary:
      "Laser-cut acrylic menu cards for in-room dining, plus large-format pavlon and hoardings for Spree Hotel & Spree Manor — matched to the property's warm signature palette.",
    services: [
      "Acrylic Menus",
      "Pavlon Boards",
      "Outdoor Hoardings",
      "Facade Branding",
    ],
    images: [
      `${P}/spree-hotel-acrylic-menu-card_2.jpeg`,
      `${P}/spree-hotel-pavlon-board.jpeg`,
      `${P}/spree-manor-hotel-hoding.jpeg`,
    ],
    accent: "#D4AF37",
  },
  "Te Amo": {
    sector: "F&B",
    title: "Restaurant identity — menu to skyline",
    summary:
      "Perfect-bound menu books and outdoor hoarding branding for the Te Amo restaurant — from the guest's first glance at the entrance to the final read at the table.",
    services: ["Menu Books", "Perfect Binding", "Outdoor Hoardings", "Signage"],
    images: [
      `${P}/te-amo-menu-book.jpeg`,
      `${P}/te-amo-restaurant-hording-branding_1.jpeg`,
      `${P}/te-amo-restaurant-hording-branding_5.jpeg`,
    ],
    accent: "#EC008C",
  },

  // ── Healthcare ─────────────────────────────────────────────────────────────
  "Medicover Hospitals": {
    sector: "Healthcare",
    title: "Wards, wayfinding, and a marathon",
    summary:
      "Complete internal branding for Medicover Hospitals — floor-by-floor wayfinding and department vinyls — plus the marathon-day backdrop and finish-line collateral for their annual community run.",
    services: [
      "Internal Branding",
      "Wayfinding",
      "Marathon Backdrops",
      "Event Collateral",
    ],
    images: [
      `${P}/medicover-hospital-internal-branding.jpeg`,
      `${P}/medicover-hospital-internal-branding_2.jpeg`,
      `${P}/medicover-marathon-backdrop.jpeg`,
    ],
    accent: "#EC008C",
  },

  // ── Retail ─────────────────────────────────────────────────────────────────
  "Reliance Digital": {
    sector: "Retail",
    title: "New store launch, wall to wall",
    summary:
      "The complete opening-day system for a Reliance Digital store — arch gate, offers hoardings, glass facade wraps, and 'now open' announcements. Delivered inside the launch window.",
    services: [
      "Arch Gates",
      "Facade Wraps",
      "Glass Film",
      "Offer Hoardings",
      "Launch Standees",
    ],
    images: [
      `${P}/reliance-digital-arch-gate.jpeg`,
      `${P}/reliance-digital-glass-facade.jpeg`,
      `${P}/reliance-digital-offers.jpeg`,
    ],
    accent: "#00AEEF",
  },
  "Apple Showroom": {
    sector: "Retail",
    title: "Selfie booth · event moment",
    summary:
      "A custom selfie-booth installation for an Apple showroom activation — printed cutouts, backdrop panels, and photo-op signage assembled on the sales floor.",
    services: ["Selfie Booth", "Cutouts", "Event Standees"],
    images: [`${P}/apple-showroom-selfibooth.jpeg`],
    accent: "#0A0A0A",
  },
  "Arriston Luxury": {
    sector: "Luxury",
    title: "Perfume packaging + retail identity",
    summary:
      "Premium packaging boxes, retail branding and combo-launch signage for Arriston Luxury Perfumes — high-gloss finishes and gold-foil details worthy of a luxury shelf.",
    services: [
      "Custom Packaging",
      "Gold Foil",
      "Retail Signage",
      "Product Branding",
    ],
    images: [
      `${P}/arriston-luxury-perfumes.png`,
      `${P}/arriston-luxury.jpg`,
    ],
    accent: "#D4AF37",
  },
  "Vail Perfume": {
    sector: "Luxury",
    title: "Storefront main board",
    summary:
      "Illuminated main-board signage for the Vail Perfume storefront — a subtle luxury reveal at street level, engineered for after-dark visibility.",
    services: ["Main Board Signage", "Illuminated Display"],
    images: [`${P}/vail-perfume-main-board.jpeg`],
    accent: "#D4AF37",
  },

  // ── Banks ──────────────────────────────────────────────────────────────────
  "Central Bank of India": {
    sector: "Banks",
    title: "Branch identity — outside and in",
    summary:
      "Exterior name board, teller-side indoor branding, and customer-facing informational panels for Central Bank of India — printed to the bank's national brand guidelines.",
    services: [
      "Name Boards",
      "Indoor Branding",
      "ACP Signage",
      "Informational Panels",
    ],
    images: [
      `${P}/central-bank-name-board.jpeg`,
      `${P}/central-bank-of-india-indoor-branding.jpeg`,
      `${P}/central-bank-of-india-indoor-branding_2.jpeg`,
    ],
    accent: "#EC008C",
  },
  "Vardhaman Bank": {
    sector: "Banks",
    title: "Signage + annual function backdrop",
    summary:
      "Branch signage plus the stage backdrop for the bank's annual function — same brand, same crisp registration, two very different applications.",
    services: ["Signage", "Event Backdrops", "Brand Guidelines"],
    images: [
      `${P}/vardhaman-bank-signage.jpeg`,
      `${P}/vardhamanbank-annul-function-backdrop.jpeg`,
    ],
    accent: "#00AEEF",
  },

  // ── Education ──────────────────────────────────────────────────────────────
  "Shamit School": {
    sector: "Education",
    title: "Kid-first brand toolkit",
    summary:
      "Branded backpacks, awards mementoes and event print — a full toolkit designed to travel home in a schoolbag and land as a story at the dinner table.",
    services: ["Backpacks", "Mementoes", "Awards", "Event Print"],
    images: [
      `${P}/shamit-school-backpack.jpeg`,
      `${P}/shamit-school-momento.jpeg`,
      `${P}/shamit-school-momento_1.jpeg`,
    ],
    accent: "#FFE500",
  },
  "Deogiri Global School": {
    sector: "Education",
    title: "Season admissions campaign",
    summary:
      "A city-wide admissions advertisement campaign for Deogiri Global School — printed on large-format flex and installed across key roadside touchpoints.",
    services: ["Outdoor Advertising", "Flex Printing", "Admissions Campaign"],
    images: [`${P}/deogiri-global-school-advertisment.jpeg`],
    accent: "#00AEEF",
  },
  "Bagga Classes": {
    sector: "Education",
    title: "Mementoes & trophies",
    summary:
      "Awards mementoes and trophy plates for Bagga Classes' annual results ceremony — engraved acrylic with printed inserts.",
    services: ["Mementoes", "Trophies", "Engraving", "Acrylic Print"],
    images: [`${P}/bagga-classes-momentos-trophy_2.jpeg`],
    accent: "#D4AF37",
  },

  // ── Industry ───────────────────────────────────────────────────────────────
  Endurance: {
    sector: "Industry",
    title: "Factory floor to town square",
    summary:
      "Indoor safety branding, outdoor building wraps, event mementoes and ID-card lanyards for Endurance — bulk industrial print with zero compromise on colour or compliance.",
    services: [
      "Indoor Branding",
      "Outdoor Wraps",
      "Mementoes",
      "ID Cards",
      "Lanyards",
    ],
    images: [
      `${P}/endurance-indoor-branding.jpeg`,
      `${P}/endurance-outdoor_branding_2.jpeg`,
      `${P}/endurance-momento-2.jpeg`,
    ],
    accent: "#FFE500",
  },
  "Varun Beverages": {
    sector: "Industry",
    title: "Outdoor board — plant edition",
    summary:
      "Weather-proof outdoor board for the Varun Beverages plant — long-life inks and eco-solvent print, spec'd for years of exterior life.",
    services: ["Outdoor Boards", "ACP", "Eco-Solvent Flex"],
    images: [`${P}/varun-beverages-outdoorl-board.jpeg`],
    accent: "#EC008C",
  },
  "Ellora Seeds": {
    sector: "FMCG",
    title: "Gift-branding pack",
    summary:
      "Custom gift-branding pack for Ellora Seeds — printed sleeves, ribbons and thank-you cards for a corporate distribution moment.",
    services: ["Gift Branding", "Sleeves", "Thank-you Cards"],
    images: [`${P}/ellora-seeds-gift-branding-_1.jpeg`],
    accent: "#FFE500",
  },
  "Dhananjay Group": {
    sector: "Events",
    title: "Outdoor event branding",
    summary:
      "Full outdoor event branding for the Dhananjay Group — arches, backdrops and directional signage installed on a live event site.",
    services: ["Event Branding", "Arches", "Backdrops", "Signage"],
    images: [`${P}/dhananjay-group-outdoor-event-branding.jpeg`],
    accent: "#00AEEF",
  },
  "Eon Vertex": {
    sector: "Corporate",
    title: "Building main board",
    summary:
      "Illuminated building main board for Eon Vertex — ACP-mounted with edge-lit acrylic letters for after-hours legibility.",
    services: ["Main Boards", "ACP", "Illuminated Signage"],
    images: [`${P}/eon-vertex-main-board_1.jpeg`],
    accent: "#00AEEF",
  },
};

export function getCaseStudy(name) {
  return CASE_STUDIES[name] || null;
}

export const BRAND_NAMES = Object.keys(CASE_STUDIES);
